import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Save, Plus, X, Globe, Bot, Phone, Shield, Loader2
} from 'lucide-react';
import AppHeader from '../components/AppHeader';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.base_role !== 'admin') {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mvp/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data);
      setDirty(false);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/mvp/admin/settings?current_user_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      if (!res.ok) throw new Error('Failed to save');
      alert('Settings saved successfully!');
      fetchSettings();
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = () => {
    if (!newDomain.trim()) return;
    const domain = newDomain.trim().toLowerCase();
    if (!settings.allowed_email_domains.includes(domain)) {
      setSettings({
        ...settings,
        allowed_email_domains: [...settings.allowed_email_domains, domain]
      });
    }
    setNewDomain('');
  };

  const removeDomain = (domain) => {
    setSettings({
      ...settings,
      allowed_email_domains: settings.allowed_email_domains.filter(d => d !== domain)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Settings" hasUnsavedChanges={dirty} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6" onChange={() => setDirty(true)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="text-blue-600" size={28} />
            <h1 className="text-2xl font-bold text-gray-800">Admin Settings</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Save Changes
          </button>
        </div>

        {/* Email Domain Restriction */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="text-green-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">Email Domain Restriction</h2>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings?.email_domain_restriction_enabled || false}
                onChange={(e) => setSettings({...settings, email_domain_restriction_enabled: e.target.checked})}
                className="rounded"
              />
              <span className="text-gray-700">Enable email domain restriction</span>
            </label>
            <p className="text-sm text-gray-500 mt-1 ml-6">
              Only allow registration/login from specified email domains
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Domains</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {settings?.allowed_email_domains?.map(domain => (
                <span 
                  key={domain}
                  className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                >
                  @{domain}
                  <button onClick={() => removeDomain(domain)} className="hover:text-green-900">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="user@example.edu.in"
                className="flex-1 px-3 py-2 border rounded-lg"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
              />
              <button
                onClick={addDomain}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Chatbot Configuration */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Bot className="text-purple-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">Digital Assistant (Chatbot)</h2>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings?.chatbot_enabled || false}
                onChange={(e) => setSettings({...settings, chatbot_enabled: e.target.checked})}
                className="rounded"
              />
              <span className="text-gray-700">Enable chatbot integration</span>
            </label>
            <p className="text-sm text-gray-500 mt-1 ml-6">
              Show 24×7 Digital Assistant section on care-seeker dashboard
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chatbot URL</label>
            <input
              type="url"
              value={settings?.chatbot_url || ''}
              onChange={(e) => setSettings({...settings, chatbot_url: e.target.value})}
              placeholder="https://your-chatbot-url.com"
              className="w-full px-3 py-2 border rounded-lg"
            />
            <p className="text-sm text-gray-500 mt-1">
              URL of external chatbot or Emergent-hosted chatbot page
            </p>
          </div>
        </div>

        {/* Care-giver Capacity */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="text-indigo-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">Care-giver Capacity</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max concurrent anonymous chats per care-giver
            </label>
            <select
              data-testid="max-concurrent-chats-select"
              value={settings?.max_concurrent_anonymous_chats ?? 2}
              onChange={(e) => setSettings({ ...settings, max_concurrent_anonymous_chats: parseInt(e.target.value, 10) })}
              className="w-40 px-3 py-2 border rounded-lg"
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Applies to anonymous chats (G0) only. Voice calls and therapy sessions are always one-at-a-time per care-giver. Recommended default: 2.
            </p>
          </div>
        </div>

        {/* Emergency Contacts */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Phone className="text-red-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">Emergency Contacts</h2>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tele-MANAS Number</label>
            <input
              type="text"
              value={settings?.tele_manas_number || ''}
              onChange={(e) => setSettings({...settings, tele_manas_number: e.target.value})}
              placeholder="14416"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Text</label>
            <textarea
              value={settings?.emergency_contact_text || ''}
              onChange={(e) => setSettings({...settings, emergency_contact_text: e.target.value})}
              placeholder="In case of emergency, please call 112..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        {/* Last Updated */}
        {settings?.updated_at && (
          <p className="text-sm text-gray-500 text-center">
            Last updated: {new Date(settings.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
